import {
  CoffeeRustAlert,
  CoffeeRustAlertResponse,
  CoffeeRustAlertPaginatedResponse,
  CoffeeRustAlertSummary,
  CalculationInput,
} from '../models/coffee-rust-alert';

/**
 * Adapter para transformar dados da API de alertas de ferrugem do café
 */
export class CoffeeRustAlertAdapter {
  /**
   * Converte resposta da API para CoffeeRustAlert
   */
  static fromApi(data: any): CoffeeRustAlert {
    return {
      id: Number(data.id) || 0,
      sadModeloId: Number(data.sadModeloId) || 0,
      weatherStationId: Number(data.weatherStationId) || 0,
      noticeDate: data.noticeDate || '',
      incidenceDate: data.incidenceDate || '',
      value: Number(data.value) || 0,
      dataCount: Number(data.dataCount) || 0,
      riskClassification: data.riskClassification || '',
      calculationInput: this.fromApiCalculationInput(data.calculationInput),
    };
  }

  /**
   * Converte dados de entrada de cálculo da API
   */
  static fromApiCalculationInput(data: any): CalculationInput {
    return {
      type: data?.type || '',
      id: Number(data?.id) || 0,
      weatherStationId: Number(data?.weatherStationId) || 0,
      referenceDate: data?.referenceDate || '',
      dataCount: Number(data?.dataCount) || 0,
      tempMin: Number(data?.tempMin) || 0,
      humidity: Number(data?.humidity) || 0,
    };
  }

  /**
   * Converte array de dados da API para array de CoffeeRustAlert
   */
  static fromApiArray(dataArray: any[]): CoffeeRustAlert[] {
    if (!Array.isArray(dataArray)) {
      return [];
    }
    return dataArray.map((data) => this.fromApi(data));
  }

  /**
   * Converte resposta paginada para CoffeeRustAlertResponse (usado pelo dashboard)
   * Mapeia totalElements como contagem de alertas favoráveis
   */
  static fromApiResponse(data: any): CoffeeRustAlertResponse {
    const content = this.fromApiArray(data.content || []);
    return {
      favorableAndVeryFavorableAlertsCount: Number(data.totalElements) || 0,
      nonFavorableAlertsCount: 0,
      nonFavorableAlerts: [],
      favorableAndVeryFavorableAlerts: content,
    };
  }

  /**
   * Converte resposta paginada da API
   */
  static fromApiPaginatedResponse(data: any): CoffeeRustAlertPaginatedResponse {
    return {
      content: this.fromApiArray(data.content || []),
      totalElements: Number(data.totalElements) || 0,
      totalPages: Number(data.totalPages) || 0,
      currentPage: Number(data.currentPage) || 0,
      hasNext: Boolean(data.hasNext),
      hasPrevious: Boolean(data.hasPrevious),
    };
  }

  /**
   * Cria resumo a partir da resposta da API
   */
  static createSummary(
    response: CoffeeRustAlertResponse,
  ): CoffeeRustAlertSummary {
    return {
      totalAlerts:
        response.favorableAndVeryFavorableAlertsCount +
        response.nonFavorableAlertsCount,
      favorableCount: response.favorableAndVeryFavorableAlertsCount,
      nonFavorableCount: response.nonFavorableAlertsCount,
      lastUpdate: new Date().toISOString(),
    };
  }

  /**
   * Mapeia classificação de risco para cor/classe CSS
   */
  static getRiskClassificationClass(riskClassification: string): string {
    const classMap: { [key: string]: string } = {
      'não favorável': 'danger',
      'nao favoravel': 'danger',
      favorável: 'warning',
      favoravel: 'warning',
      'muito favorável': 'success',
      'muito favoravel': 'success',
    };
    return classMap[riskClassification.toLowerCase()] || 'secondary';
  }

  /**
   * Obtém temperatura mínima média do alerta
   */
  static getAvgMinTemp(alert: CoffeeRustAlert): number | null {
    return alert.calculationInput?.tempMin ?? null;
  }

  /**
   * Obtém umidade média do alerta
   */
  static getAvgHumidity(alert: CoffeeRustAlert): number | null {
    return alert.calculationInput?.humidity ?? null;
  }
}
